/**
 * A file's accidentals read back down to the ones it meant.
 *
 * MusicXML writes an `<accidental>` on every note that has one drawn in front of it,
 * whether it is there because the note disagrees with the signature -- which any engraver
 * would have worked out -- or because the writer asked for it against the rules, as a
 * courtesy or an editorial mark. The document distinguishes those: a head on `auto` is one
 * whose accidental follows from the music, and a head naming its own display is one where
 * somebody overruled that.
 *
 * Taking the file at its word would make every sharp in every score an override, and the
 * document would then be unable to move a note without dragging a stale accidental along
 * with it. So each head is compared against what the rules would have printed for it, and
 * one that agrees is put back on `auto`. What is left are the accidentals the file went out
 * of its way to draw, which is exactly what the document is for.
 */

import type { AccidentalDisplay, NotePitch } from "../document/pitch";
import type { MusicEvent, Part, PartMeasure, Score } from "../document/types";
import { accidentalPlan } from "./export-accidentals";
import type { AccidentalPlan } from "./export-accidentals";

/** The name MusicXML writes for a display the document names its own way. */
function xmlName(display: AccidentalDisplay): string {
  return display === "double-flat" ? "flat-flat" : display;
}

function simplifyPitch(pitch: NotePitch, decided: string | null): NotePitch {
  if (pitch.accidental === "auto" || pitch.accidental === "none") return pitch;
  if (decided === null || xmlName(pitch.accidental) !== decided) return pitch;
  return { ...pitch, accidental: "auto" };
}

function simplifyEvent(event: MusicEvent, plan: AccidentalPlan): MusicEvent {
  if (event.kind !== "note") return event;
  const decided = plan.get(event.id);
  if (decided === undefined) return event;
  const pitches = event.pitches.map((pitch, index) => simplifyPitch(pitch, decided[index] ?? null));
  return pitches.every((pitch, index) => pitch === event.pitches[index])
    ? event
    : { ...event, pitches };
}

function simplifyMeasure(measure: PartMeasure, plan: AccidentalPlan): PartMeasure {
  return {
    ...measure,
    voices: measure.voices.map((voice) => ({
      ...voice,
      events: voice.events.map((event) => simplifyEvent(event, plan)),
    })),
  };
}

/** Put every accidental the rules would have printed anyway back on `auto`. */
export function simplifyAccidentals(score: Score): Score {
  const parts = score.parts.map((part): Part => {
    const plan = accidentalPlan(score, part);
    const measures: Record<string, PartMeasure> = {};
    for (const [id, measure] of Object.entries(part.measures)) {
      measures[id] = simplifyMeasure(measure, plan);
    }
    return { ...part, measures };
  });
  return { ...score, parts };
}
